import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PpHttpHeaders, PpHttpService } from '@penpencil/common';
import { environment } from 'environments/environment';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { ErrorHandlerService } from './error-handler.service';
import {
    FILE_UPLOAD_API,
    FILE_UPLOAD_API_AUDIO,
    FILE_UPLOAD_API_CREATE_FILE,
    FILE_UPLOAD_API_DELETE,
    FILE_UPLOAD_API_JW_PLAYER,
    FILE_UPLOAD_API_SIGNED_COOKIE,
    FILE_UPLOAD_API_VIMEO,
    FILE_UPLOAD_API_OLD,
} from './file-utils';

@Injectable({
    providedIn: 'root',
})
export class FileService extends ErrorHandlerService {
    fileUploadHeaders: {
        reportProgress: boolean;
        observe: 'body' | 'events' | 'response';
        headers: PpHttpHeaders;
    } = {
        reportProgress: true,
        observe: 'events',
        headers: environment.apiConfig.headers
            .append('Authorization', 'Bearer ' + this._authService.accessToken)
            .append('Accept', 'application/json, text/plain, */*'),
    };

    public progressPercentage: BehaviorSubject<any> = new BehaviorSubject<any>(
        0,
    );
    constructor(
        private _authService: AuthService,
        private _ppHttpService: PpHttpService,
        private _httpClient: HttpClient,
    ) {
        super();
    }

    base64ToAudio(data: any): Observable<any> {
        return this._ppHttpService
            .post(data, FILE_UPLOAD_API_AUDIO)
            .pipe(catchError(this.handleError));
    }

    uploadVideo(formData, videoType): Observable<any> {
        return this._ppHttpService
            .post(
                formData,
                videoType.toLowerCase() === 'vimeo'
                    ? FILE_UPLOAD_API_VIMEO
                    : FILE_UPLOAD_API_JW_PLAYER,
            )
            .pipe(catchError(this.handleError));
    }

    getSignedCookie(body): Observable<any> {
        return this._ppHttpService
            .post(body, FILE_UPLOAD_API_SIGNED_COOKIE)
            .pipe(catchError(this.handleError));
    }

    deleteFile(fileId): Observable<any> {
        return this._ppHttpService
            .delete(FILE_UPLOAD_API_DELETE(fileId))
            .pipe(catchError(this.handleError));
    }

    createFileObject(data): Observable<any> {
        return this._ppHttpService
            .post(data, FILE_UPLOAD_API_CREATE_FILE)
            .pipe(catchError(this.handleError));
    }

    uploadFile({
        file,
        isS3Signed = true,
        payloadKey = 'file',
    }: {
        file: File | any;
        isS3Signed?: boolean;
        payloadKey?: string;
    }): Observable<any> {
        const formData = new FormData();
        formData.append(payloadKey, file);

        const httpRequest = isS3Signed
            ? from(this.getS3SignedUrl(file))
            : this.oldFileUploadHttpRequest(formData);

        return httpRequest.pipe(
            mergeMap(async (event: HttpEvent<any>) => {
                switch (event.type) {
                    case HttpEventType.Sent:
                        this.progressPercentage.next(0);
                        return `Uploading file "${file?.name}" of size ${file?.size}.`;
                    case HttpEventType.UploadProgress:
                        const percentDone = Math.round(
                            (100 * event.loaded) / event.total,
                        );
                        this.progressPercentage.next(percentDone);
                        return `File "${file?.name}" is ${percentDone}% uploaded.`;

                    case HttpEventType.Response:
                        this.progressPercentage.next(0);

                        if (isS3Signed) {
                            const uploadResponse =
                                await this.uploadSignedURLJSON(
                                    event?.body?.data,
                                    file,
                                );

                            if (uploadResponse?.ok) {
                                const response = {
                                    data: {
                                        ...event.body.data,
                                        fullUrl:
                                            event.body.data?.baseUrl +
                                            event.body.data?.key,
                                    },
                                    success: true,
                                };
                                return response;
                            }
                        } else {
                            return event.body;
                        }
                    default:
                        return `File "${file?.name}" surprising upload event: ${event.type}.`;
                }
            }),
            catchError(this.handleError),
        );
    }

    s3UrlForUpload(file: File): string {
        return `${environment.apiConfig.baseUrl}/${
            FILE_UPLOAD_API.apiPath
        }?type=ADMIN&name=${file?.name}&extension=${(file.name || '')
            .split('.')
            .pop()}&contentType=${file?.type}`;
    }

    getS3SignedUrl(file: File): Observable<any> {
        return this._httpClient
            .request('GET', this.s3UrlForUpload(file), {
                ...this.fileUploadHeaders,
            })
            .pipe(catchError(this.handleError));
    }

    oldFileUploadHttpRequest(formData: FormData) {
        return this._httpClient.request(
            'POST',
            `${environment.apiConfig.baseUrl}/${FILE_UPLOAD_API_OLD.apiPath}`,
            {
                body: formData,
                ...this.fileUploadHeaders,
                headers: this.fileUploadHeaders.headers.append(
                    'Client-Type',
                    'ADMIN',
                ),
            },
        );
    }

    async uploadSignedURLJSON(data: any, file: File): Promise<any> {
        return await fetch(data?.uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type,
            },
        });
    }
}
